// ARQUIVO 100% COMPLETO E CORRIGIDO
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useDrag, useDrop, useDragLayer } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import useClickOutside from '../../hooks/useClickOutside';
import Modal from '../ui/Modal';
import GlassButton from '../ui/Button';
import { ChevronLeftIcon, ChevronRightIcon, RefreshIcon, SunIcon } from '../ui/Icons';
import { getPriorityColor, getStatusColor } from '../../utils/helpers';
import useData from '../../hooks/useData';
import PriorityChanger from '../ui/PriorityChanger';

export const ItemTypes = { TASK: 'task' };

export const QuickDatePicker = ({ onSelectDate, onClose }) => {
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const datePickerRef = useRef(null);
    useClickOutside(datePickerRef, onClose);

    const handleDayClick = (day) => {
        const selectedDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
        onSelectDate(selectedDate);
        onClose();
    };

    const monthNames = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
    const daysOfWeek = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];
    const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
    const firstDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay();
    let calendarDays = Array.from({ length: firstDayOfMonth }, (_, i) => <div key={`empty-${i}`}></div>);
    
    for (let day = 1; day <= daysInMonth; day++) {
        const dayDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
        const isToday = dayDate.toDateString() === new Date().toDateString();
        let dayClasses = `w-8 h-8 flex items-center justify-center rounded-full text-sm text-gray-200 transition-colors hover:bg-white/20 ${isToday ? 'bg-indigo-500 text-white font-bold' : ''}`;
        calendarDays.push(<button key={day} onClick={() => handleDayClick(day)} className={dayClasses}>{day}</button>);
    }

    return (
        <div ref={datePickerRef} className="absolute z-50 top-full mt-2 right-0 bg-gray-900/80 backdrop-blur-md border border-white/20 rounded-lg shadow-2xl p-4 w-64 animate-fade-in">
            <div className="flex items-center justify-between mb-4">
                <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))} className="p-1 rounded-full hover:bg-white/20"><ChevronLeftIcon /></button>
                <div className="font-semibold text-white">{monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}</div>
                <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))} className="p-1 rounded-full hover:bg-white/20"><ChevronRightIcon /></button>
            </div>
            <div className="grid grid-cols-7 gap-1 text-center text-xs text-gray-400 mb-2">{daysOfWeek.map((d, i) => <div key={i}>{d}</div>)}</div>
            <div className="grid grid-cols-7 gap-1">{calendarDays}</div>
        </div>
    );
};


export const CustomDragLayer = ({tasks}) => {
    const { isDragging, item, currentOffset } = useDragLayer((monitor) => ({
        item: monitor.getItem(), isDragging: monitor.isDragging(), currentOffset: monitor.getSourceClientOffset(),
    }));
    const draggedTask = useMemo(() => tasks.find(t => t.id === item?.id), [item, tasks]);
    if (!isDragging || !currentOffset) return null;
    const layerStyles = { position: 'fixed', pointerEvents: 'none', zIndex: 100, left: 0, top: 0, width: '100%', height: '100%' };
    const getItemStyles = (offset) => {
        if (!offset) return { display: 'none' };
        const { x, y } = offset;
        const transform = `translate(${x}px, ${y}px) rotate(3deg)`;
        return { transform, WebkitTransform: transform };
    }
    return (
        <div style={layerStyles}>
            <div style={getItemStyles(currentOffset)} className="w-64">
                <div className="p-2 bg-indigo-600 shadow-2xl rounded-lg text-sm text-white scale-105">
                    <p className="font-semibold uppercase">{draggedTask?.title || 'Movendo...'}</p>
                    <p className="text-xs text-indigo-200">{draggedTask?.client}</p>
                </div>
            </div>
        </div>
    );
};


export const DraggableTask = ({ task, onClick, projects = [] }) => {
    const { handleToggleMyDay } = useData();
    const [{ isDragging }, drag, preview] = useDrag(() => ({
        type: ItemTypes.TASK, item: { id: task.id }, collect: (monitor) => ({ isDragging: !!monitor.isDragging() }),
    }));
    useEffect(() => { preview(getEmptyImage(), { captureDraggingState: true }); }, [preview]);
    
    const statusColor = getStatusColor(task.status);
    const priorityColor = getPriorityColor(task.priority);

    return (
        // --- INÍCIO DA ALTERAÇÃO: Layout reestruturado para verticalizar o conteúdo ---
        <div 
            ref={drag} 
            onClick={onClick} 
            className={`group relative w-full p-3 pl-5 rounded-xl text-left text-sm transition-all duration-200 bg-gray-800 hover:bg-gray-700 ${isDragging ? 'opacity-0 scale-95' : ''}`}
        >
            <div className="absolute left-0 top-0 h-full w-1.5 rounded-l-xl overflow-hidden">
                <div style={{ backgroundColor: priorityColor }} className="h-1/2 w-full"></div>
                <div style={{ backgroundColor: statusColor }} className="h-1/2 w-full"></div>
            </div>

            <div className="flex flex-col justify-between h-12">
                <p className="font-semibold text-white text-xs truncate uppercase">{task.title}</p>

                <div className="flex items-center justify-end gap-2 h-4 opacity-0 group-hover:opacity-100 transition-opacity">
                    <PriorityChanger task={task} />
                    {task.isAlteration && (
                        <div className="text-orange-400" title="Tarefa marcada para alteração">
                            <RefreshIcon size={14} />
                        </div>
                    )}
                    <button 
                        onClick={(e) => {
                            e.stopPropagation();
                            handleToggleMyDay(task.id, task.isMyDay);
                        }}
                        title={task.isMyDay ? "Remover de Meu Dia" : "Adicionar ao Meu Dia"}
                        className={`p-1 rounded-full ${task.isMyDay ? 'bg-yellow-500/30 text-yellow-400' : 'bg-white/10 text-gray-400 hover:bg-white/20'} transition-all`}
                    >
                        <SunIcon size={14} />
                    </button>
                </div>
            </div>
        </div>
        // --- FIM DA ALTERAÇÃO ---
    );
};


export const SchedulableTask = ({ task, onScheduleClick, isSelected }) => (
    <div className="relative">
        <button onClick={onScheduleClick} className={`w-full p-2 bg-white/5 rounded-lg text-sm text-left text-white transition-colors ${isSelected ? 'bg-indigo-500' : 'hover:bg-white/10'}`}>
            <p className="font-semibold uppercase">{task.title}</p>
            <p className="text-xs text-gray-400">{task.client}</p>
        </button>
    </div>
);


export const ViewMoreModal = ({ date, tasks, onClose, onTaskSelect, projects = [] }) => {
    if (!date) return null;
    const formattedDate = date.toLocaleDateString('pt-BR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });
    return (
        <Modal isOpen={!!date} onClose={onClose}>
            <div className="p-6 text-white">
                <h2 className="text-2xl font-bold">Tarefas para</h2>
                <p className="text-indigo-300">{formattedDate}</p>
                <div className="mt-4 space-y-3 max-h-80 overflow-y-auto pr-2">
                    {tasks.map(task => {
                        const priorityColor = getPriorityColor(task.priority);
                        return (
                            <button key={task.id} onClick={() => { onTaskSelect(task); onClose(); }} className="relative w-full text-left p-3 bg-white/10 rounded-lg hover:bg-white/20 transition-colors">
                                <div className="absolute left-0 top-0 h-full w-2 rounded-l-lg" style={{ backgroundColor: priorityColor }}/>
                                <div className="pl-4">
                                    <div className="flex justify-between items-center">
                                        <p className="font-semibold uppercase">{task.title}</p>
                                        {task.isAlteration && (
                                            <div className="text-orange-400" title="Tarefa marcada para alteração">
                                                <RefreshIcon size={16} />
                                            </div>
                                        )}
                                    </div>
                                    <p className="text-sm text-gray-300">{projects.find(p => p.id === task.projectId)?.name || 'Sem projeto'}</p>
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>
            <div className="p-4 bg-black/20 flex justify-end"><GlassButton onClick={onClose}>Fechar</GlassButton></div>
        </Modal>
    );
};


export const CalendarDay = ({ day, date, tasksOnThisDay, onViewMoreClick, isToday }) => {
    const taskCount = tasksOnThisDay.length;
    const tasksToShow = 3;

    return (
        <button 
            onClick={() => onViewMoreClick(date)} 
            className="relative p-3 w-full h-full flex flex-col items-start text-left group hover:bg-white/10 transition-colors duration-200"
            disabled={taskCount === 0}
        >
            <span className={`font-semibold ${isToday ? 'text-white' : 'text-gray-300'}`}>{day}</span>
            
            {taskCount > 0 && (
                <div className="mt-1 space-y-1 w-full overflow-hidden">
                    {tasksOnThisDay.slice(0, tasksToShow).map(task => {
                        const priorityColor = getPriorityColor(task.priority);
                        return (
                            <div key={task.id} className="flex items-center gap-1.5 text-xs">
                                <div style={{ backgroundColor: priorityColor }} className="w-1.5 h-1.5 rounded-full flex-shrink-0" />
                                <span className="text-gray-300 truncate uppercase">{task.title}</span>
                                {task.isAlteration && (
                                    <div className="text-orange-400 flex-shrink-0" title="Tarefa marcada para alteração">
                                        <RefreshIcon size={12} />
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    {taskCount > tasksToShow && (
                        <div className="text-xs text-indigo-300 font-semibold mt-1">
                            + {taskCount - tasksToShow} tarefa(s)
                        </div>
                    )}
                </div>
            )}
        </button>
    );
};


export const MonthView = ({ currentDate, scheduledTasksByDate, commonProps }) => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = new Date(year, month, 1).getDay();

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const getStartOfWeek = (date) => {
        const d = new Date(date);
        const day = d.getDay();
        const diff = d.getDate() - day;
        const startOfWeek = new Date(d.setDate(diff));
        startOfWeek.setHours(0, 0, 0, 0);
        return startOfWeek;
    };

    const startOfCurrentWeek = getStartOfWeek(today);

    return (
        <>
            <div className="grid grid-cols-7 text-center text-xs font-bold text-gray-400 border-b border-white/10">{['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SÁB'].map(day => <div key={day} className="p-2">{day}</div>)}</div>
            <div className="grid grid-cols-7 grid-rows-6 flex-grow">
                {Array.from({ length: firstDayOfMonth }).map((_, i) => 
                    <div key={`empty-${i}`} className="border-l border-r border-b border-white/10"></div>
                )}
                {Array.from({ length: daysInMonth }).map((_, day) => {
                    const currentDay = day + 1;
                    const date = new Date(Date.UTC(year, month, currentDay));
                    const dateKey = date.toISOString().split('T')[0];
                    const tasksOnThisDay = scheduledTasksByDate[dateKey] || [];
                    
                    const dayDate = new Date(year, month, currentDay);
                    dayDate.setHours(0, 0, 0, 0);
                    const isToday = dayDate.getTime() === today.getTime();
                    const isCurrentWeek = getStartOfWeek(dayDate).getTime() === startOfCurrentWeek.getTime();
                    
                    let cellClasses = 'border-l border-r border-b border-white/10 transition-colors duration-300';
                    if (isToday) {
                        cellClasses += ' bg-indigo-500/30 border-2 border-indigo-500 z-10';
                    } else if (isCurrentWeek) {
                        cellClasses += ' bg-white/5';
                    }

                    return (
                        <div key={day} className={cellClasses}>
                            <CalendarDay day={currentDay} date={date} tasksOnThisDay={tasksOnThisDay} {...commonProps} isToday={isToday} />
                        </div>
                    );
                })}
            </div>
        </>
    );
};


export const WeekView = ({ currentDate, scheduledTasksByDate, onTaskClick, onDropTask, taskCapacityLimit, projects }) => {
    const getWeekDays = (date) => { const start = new Date(date); start.setDate(start.getDate() - start.getDay()); return Array.from({ length: 7 }).map((_, i) => { const day = new Date(start); day.setDate(day.getDate() + i); return day; }); };
    const weekDays = getWeekDays(currentDate);
    const todayString = new Date().toDateString();

    return (
        <div className="grid grid-cols-7 flex-grow">
            {weekDays.map((date, i) => {
                const utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
                const dateKey = utcDate.toISOString().split('T')[0];
                const tasksOnThisDay = scheduledTasksByDate[dateKey] || [];
                const isToday = date.toDateString() === todayString;
                
                const [{ isOver, canDrop }, drop] = useDrop(() => ({
                    accept: ItemTypes.TASK,
                    drop: (item) => onDropTask(item.id, utcDate),
                    collect: (monitor) => ({ isOver: !!monitor.isOver(), canDrop: !!monitor.canDrop() }),
                }));
                
                const taskCount = tasksOnThisDay.length;
                const limit = taskCapacityLimit;
                const title = `${taskCount} de ${limit} tarefas`;
                const capacityPercentage = limit > 0 ? Math.min((taskCount / limit) * 100, 100) : 0;
                let barColor = 'bg-green-500';
                if (capacityPercentage > 50) barColor = 'bg-yellow-500';
                if (capacityPercentage >= 100) barColor = 'bg-red-500';

                return (
                    <div key={i} ref={drop} className={`border-r border-white/10 flex flex-col transition-colors duration-200 ${isOver && canDrop ? 'bg-white/20' : ''}`}>
                        <div className={`text-center p-2 border-b border-white/10 ${isToday ? 'bg-indigo-500/30' : ''}`}>
                            <p className={`text-xs ${isToday ? 'text-indigo-200' : 'text-gray-400'}`}>{date.toLocaleDateString('pt-BR', { weekday: 'short' }).toUpperCase()}</p>
                            <p className={`text-2xl font-bold ${isToday ? 'text-white' : ''}`}>{date.getDate()}</p>
                        </div>
                        <div className="p-2 space-y-2 flex-grow overflow-y-auto">
                            {tasksOnThisDay.map(task => (<DraggableTask key={task.id} task={task} onClick={() => onTaskClick(task)} projects={projects} />))}
                            {isOver && canDrop && (<div className="h-12 bg-white/10 rounded-lg border-2 border-dashed border-gray-400 animate-pulse" />)}
                        </div>
                        <div className="p-2 mt-auto">
                            <div className="w-full bg-white/10 rounded-full h-1.5" title={title}>
                                <div className={`${barColor} h-1.5 rounded-full transition-all duration-300`} style={{ width: `${capacityPercentage}%` }}></div>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};